import {
  EyeOff, IdCard, Search, ShieldQuestion, TriangleAlert, UserRoundPlus, UserRoundX, type LucideIcon,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/toast";
import { ContactFormDialog } from "@/features/contact-form";
import { ApiError } from "@/lib/api";
import { callerRecordText, type CallerRecordText } from "@/lib/lead-card";
import { useCallerName, useLinkCallToLead } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { useAcdCall } from "@/softphone/acd-call";

const ICONS: Record<CallerRecordText["kind"], LucideIcon> = {
  no_record: UserRoundX,
  withheld: EyeOff,
  lookup_failed: TriangleAlert,
};

/**
 * What the ACD's lookup said about an inbound caller who has no lead card (ACL-284): we have no
 * record of the number, the caller withheld it, or the lookup failed. The lead card says who a
 * known caller is; this says why there is no card, in the card's place and its shape, so a rep does
 * not take a lookup that failed for a caller we have never heard of. A failed lookup offers the
 * search the rep would otherwise type. The number is the party's, shown above it on every screen
 * this is on.
 *
 * A caller we have no record of can be made a contact from here once the rep has taken the call
 * (ACL-285): the form has their number, the rep says who is calling, and the new contact is named
 * for the call, so the lead card takes this card's place. Nothing makes a contact without the
 * rep's click.
 *
 * The carrier may know a name for a caller with no record (CNAM, ACL-286). It is shown as the
 * carrier's and not verified, beside "No existing record", never as who is calling: the name a
 * carrier keeps is often stale, and the rep asks.
 *
 * Only for the call the ACD rang the rep for, and only while the record names no lead.
 */
export function CallerRecordStatus({ callId }: { callId: string }) {
  const acd = useAcdCall();
  // The carrier's name (ACL-286), asked only for a caller we have no record of: every ask is paid.
  const unknown =
    acd !== null && acd.callId === callId && acd.leadId === null && acd.lookupStatus === "no_lead";
  const carrier = useCallerName(unknown ? acd.callerChannelId : null);
  const carrierName = unknown ? (carrier.data?.name ?? null) : null;
  if (acd === null || acd.callId !== callId || acd.leadId !== null) return null;
  const text = callerRecordText(acd.lookupStatus);
  if (text === null) return null;
  const Icon = ICONS[text.kind];
  const warning = text.tone === "warning";
  // The number itself is the party's, above this card: the search is all it needs here.
  const digits = text.kind === "withheld" ? "" : (acd.callerNumber ?? "").replace(/\D/g, "");
  // Only once the rep has the caller: the call is theirs to name from then, not while it rings.
  const canCreate = text.kind === "no_record" && acd.state === "busy" && Boolean(acd.callerNumber);

  return (
    <section
      aria-label="CRM record"
      className={cn(
        "w-full overflow-hidden rounded-lg border text-left text-xs",
        warning ? "border-warning-4 bg-warning-2" : "border-border bg-surface",
      )}
    >
      <div className="flex items-start gap-3 px-3 py-2.5">
        <span
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-full",
            warning ? "bg-warning-4 text-warning-11" : "bg-gray-3 text-gray-11",
          )}
          aria-hidden
        >
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              "text-2xs font-semibold uppercase tracking-wide",
              warning ? "text-warning-11" : "text-text-subtle",
            )}
          >
            {text.badge}
          </p>
          <p className={cn("mt-0.5 text-sm font-semibold leading-snug", warning ? "text-warning-11" : "text-text")}>
            {text.title}
          </p>
          <p className={cn("mt-0.5 leading-snug", warning ? "text-warning-11" : "text-text-muted")}>{text.detail}</p>
          {text.kind === "lookup_failed" && digits.length > 0 && (
            <Link
              to={`/contacts?q=${encodeURIComponent(digits)}`}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-accent-11 hover:underline"
            >
              <Search className="size-3.5" aria-hidden />
              Search contacts for this number
            </Link>
          )}
          {canCreate && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2.5"
              onClick={() => askForContact({ callerChannelId: acd.callerChannelId, phone: acd.callerNumber ?? "" })}
            >
              <UserRoundPlus className="size-3.5" aria-hidden />
              Create contact
            </Button>
          )}
        </div>
      </div>
      {carrierName && (
        <div className="flex items-start gap-3 border-t border-border bg-surface-sunken px-3 py-2.5">
          <span className="flex size-8 shrink-0 items-center justify-center" aria-hidden>
            <IdCard className="size-4 text-text-subtle" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <p className="text-2xs font-semibold uppercase tracking-wide text-text-subtle">Caller name</p>
              <span
                className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border bg-surface px-1.5 py-px text-2xs font-medium text-text-muted"
                title="Carriers often keep an old or partial name for a number. Ask who is calling."
              >
                <ShieldQuestion className="size-3" aria-hidden />
                Not verified
              </span>
            </div>
            <p className="mt-0.5 truncate text-sm font-semibold text-text" title={carrierName}>
              {carrierName}
            </p>
            <p className="text-2xs text-text-subtle">From the phone carrier</p>
          </div>
        </div>
      )}
    </section>
  );
}

/** The call a "Create contact" form is open for. Kept apart from the call's screen, which a caller
 *  hanging up takes away while the rep is still typing: the call can be named after it ends. */
interface ContactAsk {
  callerChannelId: string;
  phone: string;
}

let asked: ContactAsk | null = null;
const watchers = new Set<() => void>();

function setAsked(next: ContactAsk | null): void {
  asked = next;
  for (const notify of watchers) notify();
}

export function askForContact(ask: ContactAsk): void {
  setAsked(ask);
}

function useAsked(): ContactAsk | null {
  return React.useSyncExternalStore(
    (notify) => {
      watchers.add(notify);
      return () => {
        watchers.delete(notify);
      };
    },
    () => asked,
    () => asked,
  );
}

/**
 * The "Create contact" form for an unknown caller (ACL-285), on the phone's outermost part so it
 * outlives the call screen it was opened from: the form has the caller's number, the rep says who
 * is calling, and the new contact (or the record that already has the number) is named for the call.
 * The contact is saved first; a call that could not be named is said so, with the way to finish.
 */
export function CallerContactForm() {
  const ask = useAsked();
  const toast = useToast();
  const link = useLinkCallToLead();
  if (ask === null) return null;

  const linkTo = (leadId: string) =>
    link.mutate(
      { callerChannelId: ask.callerChannelId, leadId },
      {
        onSuccess: () => toast.success("Call linked to the contact"),
        onError: (e) =>
          toast.error(
            "The contact was saved but not linked to this call",
            `${e instanceof ApiError ? `${e.message} ` : ""}Create contact again and choose Link this call to it.`,
          ),
      },
    );

  return (
    <ContactFormDialog
      open
      onOpenChange={(open) => !open && setAsked(null)}
      fromCall={{ phone: ask.phone }}
      onCreated={(card) => linkTo(card.id)}
      onUseExisting={(leadId) => {
        setAsked(null);
        linkTo(leadId);
      }}
    />
  );
}
